import { DateUtils } from "@/lib/utils/date";
import { defaultStoreCore as defaultStore } from "@/stores/default-store.core";
import {
   convertDetachedDayToUtcDateWithoutTimezoneOffset,
   getDetachedDay,
} from "@laborchart-modules/common/dist/datetime";
import { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { SendOneOffProjectAlertsPayload } from "@laborchart-modules/lc-core-api/dist/api/alerts/send-one-off-project-alerts";
import type { SerializedFindAssignment } from "@laborchart-modules/lc-core-api/dist/api/assignments";
import { stripAndReturnHTMLTokens } from "../../communications/dynamic-tokens/token-utils";
import type { SerializedFindTimeOff } from "@laborchart-modules/lc-core-api/dist/api/time-off/find-time-off";
import type {
   AssignmentFormattedTypes,
   AssignmentListData,
   FormattedAssignmentData,
} from "./types";

import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import type { SerializedFindEquipmentAssignment } from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/find-equipment-assignment";
import { formatDateTime } from "@/react/shared/helper";
import type { useI18nContext } from "@procore/core-react";
import { validateWorkDayTimeAndAllocationForBulkAssignmentAndRequest } from "../helper";
import { ResourceType } from "@/react/prop-types";

function formatAssignmentDetails(
   id: string,
   content: string,
   start_day: number,
   end_day: number,
): AssignmentFormattedTypes {
   return {
      assignmentId: id,
      formattedAssignmentDetails: `${content}, ${DateUtils.formatDetachedDay(
         start_day,
         defaultStore.getDateFormat(),
      )} - ${DateUtils.formatDetachedDay(end_day, defaultStore.getDateFormat())}`,
      start_day,
   };
}

export function sortByStartDay(
   data: AssignmentFormattedTypes[],
   sortOrder: Order = Order.ASCENDING,
): AssignmentFormattedTypes[] {
   return data.sort((a, b) => {
      const dateA = new Date(a.start_day).getTime();
      const dateB = new Date(b.start_day).getTime();
      return sortOrder === Order.ASCENDING ? dateA - dateB : dateB - dateA;
   });
}

export function getCurrentAssignmentsFormatted(
   currentAssignments: SerializedFindAssignment[] | undefined,
   scheduledTimeOff: SerializedFindTimeOff[] | null,
): AssignmentFormattedTypes[] | undefined {
   if (!currentAssignments && !scheduledTimeOff) return undefined;

   const formattedAssignments =
      currentAssignments?.map((assignment) =>
         formatAssignmentDetails(
            assignment.id,
            assignment.project.name,
            assignment.start_day,
            assignment.end_day,
         ),
      ) ?? [];

   const formattedTimeOff =
      scheduledTimeOff?.map((timeOff) =>
         formatAssignmentDetails(timeOff.id, timeOff.reason, timeOff.start_day, timeOff.end_day),
      ) ?? [];

   return sortByStartDay([...formattedAssignments, ...formattedTimeOff]);
}

export function getEquipmentAssignmentSchedulesFormatted(
   equipmentAssignments: SerializedFindEquipmentAssignment[] | undefined,
): AssignmentFormattedTypes[] | undefined {
   if (!equipmentAssignments) return undefined;

   const formattedData = equipmentAssignments?.map((assignment) =>
      formatAssignmentDetails(
         assignment.id,
         assignment.project.name,
         assignment.start_day,
         assignment.end_day,
      ),
   );

   return sortByStartDay(formattedData);
}

export function formatOneOffAlertsPayload(
   alertContent: any,
   formValues: any,
   shouldScheduleDelivery: boolean,
   projectId: string,
   recipientsToIgnore?: string[],
): SendOneOffProjectAlertsPayload {
   const { content: apiReadySubject, dynamicTokens: subjectTokens } = stripAndReturnHTMLTokens(
      alertContent.subject,
      projectId,
   );
   const { content: apiReadyContent, dynamicTokens: contentTokens } = stripAndReturnHTMLTokens(
      alertContent.content,
      projectId,
   );

   const dynamicTokens = subjectTokens.concat(contentTokens);
   const template = {
      content: apiReadyContent,
      subject: apiReadySubject,
      dynamic_tokens: dynamicTokens,
      is_group: false,
      owner_id: projectId,
      type: "assignment-new",
      include_signature: false,
   };

   const payload: SendOneOffProjectAlertsPayload = {
      assignments_start_day: getDetachedDay(formValues.start_date),
      assignments_end_day: getDetachedDay(formValues.end_date),
      context: AlertContext.OPEN,
      people_ids_to_ignore: recipientsToIgnore ?? [],
      project_id: projectId,
      template: template,
   };

   if (shouldScheduleDelivery) {
      payload.context = AlertContext.SCHEDULED;
      payload.scheduled_day = getDetachedDay(formValues.scheduled_day);
      payload.scheduled_time =
         formValues.day_night.value === "pm"
            ? formValues.hour.value + formValues.minute.value + 12
            : formValues.hour.value + formValues.minute.value;
   }

   return payload;
}

export function formatTime(time: string | number | null): string | null {
   if (typeof time === "number") {
      const hours = Math.floor(time).toString().padStart(2, "0");
      const minutes = Math.floor((time % 1) * 60)
         .toString()
         .padStart(2, "0");
      return `${hours}:${minutes}:00`;
   }
   return time;
}

export function processAssignmentDatesAndTime(assignment: {
   start_day: number;
   end_day: number;
   start_time: number | null;
   end_time: number | null;
}) {
   return {
      start_day: DateUtils.getAttachedDate(assignment.start_day),
      end_day: DateUtils.getAttachedDate(assignment.end_day),
      start_time: formatTime(assignment.start_time),
      end_time: formatTime(assignment.end_time),
   };
}

export const validateDateForBulkAssignment = (
   assignment: AssignmentListData,
   formStartDay: number | null,
   formEndDay: number | null,
   I18n: ReturnType<typeof useI18nContext>,
): string[] => {
   const reasons: string[] = [];

   if (formStartDay && assignment.end_day && assignment.end_day < formStartDay) {
      reasons.push(
         I18n.t("views.company.workforce_planning.assignment.bulk.edit.validation.start_date", {
            date: formatDateTime(assignment.end_day, {
               locale: I18n.locale,
               isRequiredTimezone: false,
            }),
         }),
      );
   }

   if (formEndDay && assignment.start_day && assignment.start_day > formEndDay) {
      reasons.push(
         I18n.t("views.company.workforce_planning.assignment.bulk.edit.validation.end_date", {
            date: formatDateTime(assignment.start_day, {
               locale: I18n.locale,
               isRequiredTimezone: false,
            }),
         }),
      );
   }

   return reasons;
};

export const validateAssignments = (
   assignments: AssignmentListData[],
   formStartDay: number | null,
   formEndDay: number | null,
   isFormByAllocation: boolean,
   isFormByTime: boolean,
   I18n: ReturnType<typeof useI18nContext>,
): {
   validAssignments: AssignmentListData[];
   invalidAssignments: Array<{ assignment: AssignmentListData; reason: string }>;
} => {
   const validAssignments: AssignmentListData[] = [];
   const invalidAssignments: Array<{ assignment: AssignmentListData; reason: string }> = [];

   assignments.forEach((assignment) => {
      // validate dates
      const dateReasons = validateDateForBulkAssignment(assignment, formStartDay, formEndDay, I18n);
      // validate workday, time, and allocation
      const otherReasons = validateWorkDayTimeAndAllocationForBulkAssignmentAndRequest(
         assignment,
         formStartDay,
         formEndDay,
         isFormByAllocation,
         isFormByTime,
         I18n,
      );
      const reasons = [...dateReasons, ...otherReasons];

      if (reasons.length === 0) {
         validAssignments.push(assignment);
      } else {
         reasons.forEach((reason) => {
            invalidAssignments.push({ assignment, reason });
         });
      }
   });

   return { validAssignments, invalidAssignments };
};

export function getCurrentNextAssignmentsFormatted(
   currentNextAssignments: SerializedFindAssignment[] | undefined,
): FormattedAssignmentData[] | undefined {
   if (!currentNextAssignments) return undefined;

   const formattedAssignments =
      currentNextAssignments?.map(({ id, project, person, start_day, end_day, work_days }) => ({
         id,
         project_name: project.name,
         project_id: project.id,
         job_title: {
            label: person.job_title?.name ?? "",
            color: person.job_title?.color ?? "",
            shape: "circle",
            id: person.job_title?.id ?? "",
         },
         start_date: convertDetachedDayToUtcDateWithoutTimezoneOffset(start_day),
         end_date: convertDetachedDayToUtcDateWithoutTimezoneOffset(end_day),
         assignment_type: ResourceType.LABOR,
         work_days_object: work_days,
         work_days: DateUtils.getFormattedWorkDays(work_days),
      })) ?? [];

   return formattedAssignments;
}
